/**
 * Who lives here, as content declares them (M4-08, cast completed in M5-01).
 *
 * The *schema* half of the villagers, and a leaf: it names nothing but ids and
 * coordinates, so `catalogue.ts` can carry a `villager()` lookup without the
 * content seam dragging the whole rule set in behind it. The rules — their day,
 * their services, the conversation — are `village.ts`'s, exactly as the
 * pharmacopoeia's numbers are `herbs.ts`'s and what a poultice *does* is
 * `interact.ts`'s.
 *
 * A villager is three things and no more: a **schedule**, a **voice**, and a
 * short list of **services**. There are no needs, no goals and no simulated
 * inner life, because the design spec's full NPC simulation is post-MVP and a
 * half-built one would be a lie told in code. What makes the Meddyg the healer
 * and the Smith the smith is entirely in the data below — which is the whole
 * claim M4-08 made when it built this for one NPC, and the claim M5-01 cashes
 * by adding four more without adding a system.
 *
 * They are all **killable and none of them come back**. They are actors with
 * bodies like everybody else, and a valley that has lost its healer has lost
 * its healer. That consequence is the game, not a bug.
 */
/**
 * The things you can ask somebody who lives here for.
 *
 * Menu-driven; no free text (M4-08), and one flat list rather than a list per
 * villager, because the *conversation* is one conversation: every topic below
 * refuses for the same reasons (they are not there, they are asleep, they are
 * dead) and is validated against the same vocabulary. Which of them a given
 * villager will actually answer is `VillagerDef.topics`, which is data.
 */
export const TOPICS = [
    'greet',
    'rumour',
    // M4-08. The Meddyg's two: bind me up, and may I lie down.
    'treat',
    'bed',
    // M5-01. The Smith's three. `trade` reads the stall out, `buy` names one
    // thing off it, and `repair` puts an edge back on what you are carrying.
    // `buy` is separate from `trade` for the reason `aim` is separate from
    // `attack`: seeing the prices must not commit you to paying one.
    'trade',
    'buy',
    'repair',
    // Contextual errand responses are authored by `ErrandDef.dialogue`, not by a
    // villager's permanent services. It is still a topic on the wire so the same
    // validated talk intent carries a clicked response back to the sim.
    'errand',
];
export function isTopic(value) {
    return typeof value === 'string' && TOPICS.includes(value);
}
/* -------------------------------------------------------------------------- *
 * Their day, resolved
 * -------------------------------------------------------------------------- */
/** Where this villager should be right now, or `null` if their day says nothing. */
export function stopFor(source, villager, hour) {
    const def = source.villager?.(villager.defId);
    if (!def)
        return null;
    let chosen = null;
    for (const stop of def.schedule) {
        if (stop.fromHour <= hour && (!chosen || stop.fromHour > chosen.fromHour))
            chosen = stop;
    }
    // Before the first stop of the day is the *last* stop of the night before.
    if (!chosen) {
        for (const stop of def.schedule) {
            if (!chosen || stop.fromHour > chosen.fromHour)
                chosen = stop;
        }
    }
    return chosen ? (villager.places[chosen.anchor] ?? null) : null;
}
//# sourceMappingURL=villagers.js.map